import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { BottomCTA, IconXmarkLine, RadioGroup, TextField, TopNavigation } from "@hamlak/ui";
import { isApiError } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import { t } from "@/i18n/ko";
import { reasonOptions, reportReasonText } from "@/i18n/codes";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import "./reportScreen.css";

const DETAIL_MAX = 300;

/**
 * 신고 대상. label = 제목 아래 대상 표시.
 * turn = 캐릭터 메시지 id(서버 turns.id), "{캐릭터명} · 마지막 응답". plot = 플롯 프로필(slug), "{플롯 제목} · 플롯"(AC0-270).
 */
export type ReportTarget =
  | { kind: "turn"; conversationId: string; turnId: string; label: string }
  | { kind: "plot"; plotKey: string; label: string };

type Props = {
  open: boolean;
  /** 없으면 화면을 열지 않는다. */
  target: ReportTarget | null;
  onClose: () => void;
};

/**
 * 1.21 신고(F04-05 80:2430). 전체 화면 오버레이(시트 스택 "report" — 뒤로가기는 셸이 닫는다): TopNavigation(X · "신고하기") →
 * t9 "어떤 문제가 있나요?" → 대상 표시 → RadioGroup(configStore.reportReasons) → 상세(≤300, 선택) → BottomCTA "신고하기".
 * POST …/turns/:turnId/reports 또는 /plots/:key/reports → 201 → 닫기 + Snackbar(report.done). 409 REPORT_DUPLICATE → 닫기 + Snackbar(report.duplicate·duplicatePlot). 오프라인·그 외 → 화면 유지 + Snackbar.
 */
export function ReportScreen({ open, target, onClose }: Props) {
  const reasons = useConfigStore((s) => s.settings?.reportReasons ?? []);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [reason, setReason] = useState<string | null>(null);
  const [detail, setDetail] = useState("");

  const reset = () => {
    setReason(null);
    setDetail("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (!target || !reason) throw new Error("report target missing");
      const trimmed = detail.trim();
      const body = trimmed ? { reason, detail: trimmed } : { reason };
      if (target.kind === "plot") {
        return call(api.POST("/plots/{id}/reports", { params: { header: xClient(), path: { id: target.plotKey } }, body }));
      }
      return call(
        api.POST("/conversations/{id}/turns/{turnId}/reports", {
          params: { header: xClient(), path: { id: target.conversationId, turnId: target.turnId } },
          body,
        }),
      );
    },
    onSuccess: () => {
      close();
      showSnackbar(t("report.done"), undefined, "positive");
    },
    onError: (e) => {
      if (isApiError(e) && e.status === 409 && e.code === "REPORT_DUPLICATE") {
        close();
        showSnackbar(t(target?.kind === "plot" ? "report.duplicatePlot" : "report.duplicate"));
        return;
      }
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("report.failed"));
    },
  });

  if (!open || !target) return null;
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="report-heading" className="report" data-testid="report-screen">
      <div className="page report__page">
        <TopNavigation
          title={t("report.title")}
          left={
            <button type="button" className="report__close" aria-label={t("common.close")} onClick={close}>
              <IconXmarkLine size={24} />
            </button>
          }
          right={null}
        />
        <div className="report__body">
          <h2 id="report-heading" className="report__heading">
            {t("report.description")}
          </h2>
          <p className="report__target">{target.label}</p>
          <RadioGroup aria-label={t("report.reason")} options={reasonOptions(reasons, reportReasonText)} value={reason} onChange={setReason} />
          <TextField multiline rows={3} label={t("report.detail")} placeholder={t("report.detail.placeholder")} value={detail} onValueChange={setDetail} maxLength={DETAIL_MAX} />
        </div>
        <BottomCTA primary={{ children: t("report.cta"), disabled: !reason || submit.isPending, loading: submit.isPending, onClick: () => submit.mutate() }} />
      </div>
    </div>
  );
}
